import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { AiInterviewRound, AiInterviewRoundsResponse } from '@edtech-ui/core/models/ai-interview/interview-round';
import { InterviewHistoryItem, InterviewHistoryResponse } from '@edtech-ui/core/models/ai-interview/interviewhistory';
import { MockInterviewPracticeHistoryResponse, PracticeHistory } from '@edtech-ui/core/models/ai-interview/mock-interview-history';
import { AiInterviewServiceService } from '@edtech-ui/core/services/ai-interview-service/ai-interview-service.service';
import { DeleteDialogBoxComponent } from '@edtech-ui/dashboard/teacher/classroom/delete-dialog-box/delete-dialog-box.component';
import { environment } from 'environments/environment';
import { ToastrService } from 'ngx-toastr';
import { PaginatorModule } from 'primeng/paginator';
import { debounceTime, Subject, takeUntil, tap } from 'rxjs';

export interface UnifiedInterviewHistoryItem {
  sessionId: string;
  mode: 'company' | 'ai';
  role: string;
  roundBadge: string;
  candidateName: string;
  displayDate: string;
  rawDate: any;
  timestamp: number;
  displayDuration: string;
  score: number;
  color: string;
  status: string;
  statusClass: string;
  isCompleted: boolean;
  canDelete: boolean;
  rawPracticeItem?: PracticeHistory;
  rawAiItem?: DashboardInterview;
}

interface DashboardInterview {
  session_id: string;
  role: string;
  type: string;
  name: string;
  date: string;
  rawDate?: any;
  duration: string;
  score: number;
  color: string;
  status: string;
  round?: string;
}

@Component({
  selector: 'app-ai-interview-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginatorModule, DeleteDialogBoxComponent, MatDialogModule, HasPermissionDirective],
  templateUrl: './ai-interview-dashboard.component.html',
  styleUrl: './ai-interview-dashboard.component.scss'
})
export class AiInterviewDashboardComponent {
  interviews: DashboardInterview[] = [];
  combinedHistory: UnifiedInterviewHistoryItem[] = [];
  interviewRounds: string[] = [];

  // State
  page = 1;
  pageSize = 10;
  totalRecords = 0;
  loading = false;
  searchTerm = '';
  selectedRound = 'All';

  historyFilter: 'all' | 'company' | 'ai' = 'all';
  isHistoryFilterOpen = false;
  historyPage = 1;
  historyPageSize = 10;

  PermissionForStudents = PermissionForStudents;
  activeTab: 'mock' | 'history' = 'mock';
  mockInterviews: any[] = [];
  mockSearchTerm = '';
  mockSelectedCategory = 'All';
  mockLoading = false;
  mockPage = 1;
  mockPageSize = 9;
  mockTotalRecords = 0;
  mockInterview: PracticeHistory[] = [];
  mockInterviewPage = 1;
  mockInterviewPageSize = 10;
  mockInterviewTotalRecords = 0;
  mockInterviewLoading = false;

  private destroy$ = new Subject<void>();
  private refreshTrigger$ = new Subject<void>();
  private mockSearchTrigger$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private aiService: AiInterviewServiceService,
    private toast: ToastrService,
    private dialog: MatDialog,
    private http: HttpClient
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const tab = params['tab'];

      if (tab === 'history') {
        this.activeTab = 'history';
      } else {
        this.activeTab = 'mock';
      }
    });
    this.fetchMockInterviews();
    this.setupRefreshTrigger();
    this.setupMockSearchTrigger();
    this.loadInterviewRounds();
    this.fetchInterviewHistory();
    this.getAllMockInterViewHistory();
  }

  setupRefreshTrigger() {
    this.refreshTrigger$
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.historyFilter === 'all') {
          this.fetchInterviewHistory();
          this.getAllMockInterViewHistory();
        } else if (this.historyFilter === 'company') {
          this.getAllMockInterViewHistory();
        } else if (this.historyFilter === 'ai') {
          this.fetchInterviewHistory();
        }
      });
  }

  loadInterviewRounds() {
    this.aiService.getInterviewRounds()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: AiInterviewRoundsResponse) => {
          if (res.Success && Array.isArray(res.Data)) {
            const rounds = res.Data.map(r => r.job_role || (r as any).round_name || (r as any).name).filter(Boolean);
            this.interviewRounds = ['All', ...new Set(rounds)];
          } else {
            this.interviewRounds = ['All'];
          }
        },
        error: (err) => {
          console.error('Failed to load rounds:', err);
          this.interviewRounds = ['All'];
        }
      });
  }

  fetchInterviewHistory() {
    this.loading = true;
    this.aiService.getInterviewHistory(
      this.page,
      this.pageSize,
      this.searchTerm,
      this.selectedRound === 'All' ? undefined : this.selectedRound
    ).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.loading = false;
          if (res && (res.Success || res.Data || res.data || res.interviews)) {
            const resData = res?.Data || res?.data || res;
            const rawList = resData?.interviews ||
                            resData?.history ||
                            resData?.rounds ||
                            (Array.isArray(resData) ? resData : null) ||
                            res?.interviews ||
                            (Array.isArray(res) ? res : []);

            this.interviews = (rawList || []).map((h: any) => {
              const score = h.overall_score !== undefined && h.overall_score !== null
                ? Number(h.overall_score)
                : (h.score !== undefined && h.score !== null ? Number(h.score) : 0);

              const status = h.status || this.getPerformanceStatus(score);
              const rawDate = h.completed_at || h.interview_date || h.created_at || h.date || h.updated_at;

              return {
                session_id: h.session_id || h.id || h._id || '',
                role: h.role_title || h.job_role || h.role || 'Junior Developer',
                type: h.type || (h.is_teacher_role ? 'Teacher Assessment' : 'Technical Interview'),
                name: h.interviewer_name || h.candidate_name || h.name || 'Candidate',
                date: this.formatDate(rawDate),
                rawDate: rawDate,
                duration: this.formatDuration(h.duration_minutes ?? h.duration),
                score: Math.min(Math.max(score, 0), 100),
                color: this.getScoreColor(score, status),
                status: status,
                round: h.interview_round || h.screening_round_id || h.round_id || h.round || 'Warm Up Round'
              };
            });

            this.totalRecords = resData?.total_count ??
                                resData?.pagination?.total ??
                                res?.total_count ??
                                this.interviews.length;
            this.updateCombinedHistory();
          } else {
            this.interviews = [];
            this.totalRecords = 0;
            this.updateCombinedHistory();
          }
        },
        error: (err) => {
          this.loading = false;
          console.error('Error fetching history:', err);
          this.toast.error('Failed to load interview history');
          this.updateCombinedHistory();
        }
      });
  }


  getAllMockInterViewHistory(): void {
    this.mockInterviewLoading = true;
    this.aiService.getMockInterviewPracticeHistory(this.mockInterviewPage, this.mockInterviewPageSize, this.searchTerm)
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: (response: MockInterviewPracticeHistoryResponse) => {
          this.mockInterviewLoading = false;
          if (response?.Success && response?.Data?.history) {
            this.mockInterview = response.Data.history;
            this.mockInterviewTotalRecords = response.Data.pagination?.total ?? response.Data.history.length;
          } else {
            this.mockInterview = [];
            this.mockInterviewTotalRecords = 0;
          }
          this.updateCombinedHistory();
        },
        error: (err) => {
          this.mockInterviewLoading = false;
          console.error('Error fetching mock practice history:', err);
          this.mockInterview = [];
          this.mockInterviewTotalRecords = 0;
          this.toast.error('Failed to load practice history');
          this.updateCombinedHistory();
        }
      });
  }

  get filteredMockInterview(): PracticeHistory[] {
    if (!this.searchTerm?.trim()) {
      return this.mockInterview;
    }
    const term = this.searchTerm.toLowerCase().trim();
    return (this.mockInterview || []).filter(item =>
      (item.job_role && item.job_role.toLowerCase().includes(term)) ||
      (item.candidate_name && item.candidate_name.toLowerCase().includes(term)) ||
      (item.status && item.status.toLowerCase().includes(term))
    );
  }

  onMockInterviewPageChange(event: any): void {
    this.onUnifiedPageChange(event);
  }

  getProgressWidth(score: number | null | undefined): number {
    const s = Number(score) || 0;
    return Math.min(Math.max(s, 0), 100);
  }

  formatStatus(status?: string, score?: number): string {
    if (!status) return this.getPerformanceStatus(score || 0);
    const s = status.trim().toLowerCase();
    if (s === 'fail') return 'Fail';
    if (s === 'pass') return 'Pass';
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  formatDate(rawDate: any): string {
    if (!rawDate) return new Date().toLocaleDateString();
    const d = new Date(rawDate);
    if (isNaN(d.getTime())) return String(rawDate);
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  }

  formatDuration(rawDuration: any): string {
    if (rawDuration === undefined || rawDuration === null) return '0 min';
    if (typeof rawDuration === 'string' && rawDuration.includes('min')) return rawDuration;
    const num = Number(rawDuration);
    if (isNaN(num)) return '0 min';
    const mins = num > 60 ? Math.round(num / 60) : num;
    return `${mins} min`;
  }

  getScoreColor(score: number, status?: string): string {
    const s = (status || '').toLowerCase();
    if (s.includes('need') || s.includes('poor') || s.includes('fail')) return '#ef4444';
    if (s.includes('average') || s.includes('fair')) return '#f97316';
    if (s.includes('good') || s.includes('pass')) return '#10b981';
    if (s.includes('excellent')) return '#16a34a';

    if (score >= 80) return '#16a34a';
    if (score >= 60) return '#10b981';
    if (score >= 40) return '#f97316';
    return '#ef4444';
  }

  getPerformanceStatus(score: number): string {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Average';
    return 'Needs Improvement';
  }

  getStatusClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (s.includes('need') || s.includes('poor') || s.includes('fail')) return 'needs-improvement';
    if (s.includes('average') || s.includes('fair')) return 'average';
    if (s.includes('good') || s.includes('pass')) return 'good';
    if (s.includes('excellent')) return 'excellent';
    return 'needs-improvement';
  }

  startInterview() {
    this.router.navigate(['/student-dashboard/ai-setup-wizard']);
  }

  viewDetailedReport(sessionId: string) {
    this.router.navigate(['/student-dashboard/ai-interview-history'], {
      queryParams: { session_id: sessionId , tab:'history' }
    });
  }

  viewPracticeReport(interview: PracticeHistory): void {
    const sessionId = interview.front_end_session_id || interview.session_id;
    const candidateId = interview.candidate_id;
    const token = interview.token || 'none';

    if (!sessionId || !candidateId) {
      this.toast.warning('Session details missing for this practice interview.');
      return;
    }

    this.router.navigate(['/ai-performance-report', candidateId, sessionId, token], {
      queryParams: { tab: 'history' } });
  }

  deleteInterview(sessionId: string): void {
    const dialogRef = this.dialog.open(DeleteDialogBoxComponent, {
      width: '400px',
      data: {
        title: 'Delete Interview',
        message: 'Are you sure you want to delete this interview? This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.loading = true;
          this.aiService.deleteInterview(sessionId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res) => {
                this.loading = false;
                if (res.Success) {
                  this.toast.success(res.Message || 'Interview deleted successfully');
                  this.interviews = this.interviews.filter(i => i.session_id !== sessionId);
                  this.mockInterview = this.mockInterview.filter(i => i.session_id !== sessionId);
                  if (this.totalRecords > 0) this.totalRecords--;
                  if (this.mockInterviewTotalRecords > 0) this.mockInterviewTotalRecords--;
                  this.updateCombinedHistory();
                } else {
                  this.toast.warning(res.Message || 'Could not delete interview');
                }
              },
              error: (err) => {
                this.loading = false;
                console.error('Delete error:', err);
                this.toast.error('Server error deleting interview');
              },
            });
        }
      });
  }

  viewReport(item: UnifiedInterviewHistoryItem): void {
    if (item.mode === 'company' && item.rawPracticeItem) {
      this.viewPracticeReport(item.rawPracticeItem);
    } else {
      this.viewDetailedReport(item.sessionId);
    }
  }

  private parseDateToTimestamp(rawDate: any): number {
    if (!rawDate) return 0;
    if (typeof rawDate === 'number') return rawDate;
    const parsed = new Date(rawDate).getTime();
    return isNaN(parsed) ? 0 : parsed;
  }

  private checkIfCompleted(status?: string, score?: number): boolean {
    const s = (status || '').trim().toLowerCase();
    if (
      s.includes('incomplet') ||
      s.includes('in progress') ||
      s.includes('in_progress') ||
      s.includes('progress') ||
      s.includes('pending') ||
      s.includes('abort') ||
      s.includes('cancel') ||
      s.includes('start')
    ) {
      return false;
    }
    if (
      s.includes('evaluat') ||
      s.includes('complet') ||
      s.includes('finish') ||
      s.includes('pass') ||
      s.includes('fail') ||
      s.includes('excellent') ||
      s.includes('good') ||
      s.includes('average') ||
      s.includes('needs improvement')
    ) {
      return true;
    }
    return score !== undefined && score !== null && score > 0;
  }

  private sortCombinedList(items: UnifiedInterviewHistoryItem[]): UnifiedInterviewHistoryItem[] {
    return items.sort((a, b) => {
      // 1. Completed interviews come before incomplete interviews
      const aComp = a.isCompleted ? 1 : 0;
      const bComp = b.isCompleted ? 1 : 0;
      if (aComp !== bComp) {
        return bComp - aComp;
      }

      // 2. Whichever was completed/attended most recently (highest timestamp) comes first
      if (b.timestamp !== a.timestamp) {
        return b.timestamp - a.timestamp;
      }

      // 3. Tie-break by score
      return (b.score || 0) - (a.score || 0);
    });
  }

  updateCombinedHistory(): void {
    const items: UnifiedInterviewHistoryItem[] = [];
    const seenIds = new Set<string>();

    // 1. Include Practice History items if filter is 'all' or 'company'
    if (this.historyFilter === 'all' || this.historyFilter === 'company') {
      const practiceList = this.mockInterview || [];
      for (const p of practiceList) {
        const sid = p.front_end_session_id || p.session_id || '';
        if (sid && seenIds.has(sid)) continue;
        if (sid) seenIds.add(sid);

        const rawDate = p.created_at || (p as any).interview_date || (p as any).date || (p as any).completed_at;
        const timestamp = this.parseDateToTimestamp(rawDate);
        const score = Number(p.overall_score) || 0;
        const status = this.formatStatus(p.status, score);
        const isCompleted = this.checkIfCompleted(p.status, score);

        items.push({
          sessionId: sid,
          mode: 'company',
          role: p.job_role || 'Mock Interview',
          roundBadge: p.is_teacher_role ? 'Teacher Assessment' : 'Practice Session',
          candidateName: p.candidate_name || 'Candidate',
          displayDate: this.formatDate(rawDate),
          rawDate: rawDate,
          timestamp: timestamp,
          displayDuration: this.formatDuration(p.duration),
          score: Math.min(Math.max(score, 0), 100),
          color: this.getScoreColor(score, p.status),
          status: status,
          statusClass: this.getStatusClass(p.status || status),
          isCompleted: isCompleted,
          canDelete: false,
          rawPracticeItem: p
        });
      }
    }

    // 2. Include AI Mock Interview items if filter is 'all' or 'ai'
    if (this.historyFilter === 'all' || this.historyFilter === 'ai') {
      const aiList = this.interviews || [];
      for (const a of aiList) {
        const sid = a.session_id;
        if (sid && seenIds.has(sid)) continue;
        if (sid) seenIds.add(sid);

        const rawDate = a.rawDate || a.date;
        const timestamp = this.parseDateToTimestamp(rawDate);
        const score = Number(a.score) || 0;
        const status = this.formatStatus(a.status, score);
        const isCompleted = this.checkIfCompleted(a.status, score);

        items.push({
          sessionId: sid,
          mode: 'ai',
          role: a.role || 'Junior Developer',
          roundBadge: a.round || 'Warm Up Round',
          candidateName: a.name || 'Candidate',
          displayDate: a.date,
          rawDate: rawDate,
          timestamp: timestamp,
          displayDuration: a.duration,
          score: Math.min(Math.max(score, 0), 100),
          color: a.color || this.getScoreColor(score, a.status),
          status: status,
          statusClass: this.getStatusClass(a.status || status),
          isCompleted: isCompleted,
          canDelete: true,
          rawAiItem: a
        });
      }
    }

    // 3. Client-side search filtering
    let filtered = items;
    if (this.searchTerm?.trim()) {
      const term = this.searchTerm.toLowerCase().trim();
      filtered = items.filter(item =>
        (item.role && item.role.toLowerCase().includes(term)) ||
        (item.candidateName && item.candidateName.toLowerCase().includes(term)) ||
        (item.roundBadge && item.roundBadge.toLowerCase().includes(term)) ||
        (item.status && item.status.toLowerCase().includes(term))
      );
    }

    // 4. Sort: Completed first, then newest timestamp first
    this.combinedHistory = this.sortCombinedList(filtered);
  }

  getScore(score: number | null | undefined): number {
    return Math.floor(score || 0);
  }

  setTab(tab: 'mock' | 'history') {
    this.activeTab = tab;
    if (tab === 'history') {
      if (this.historyFilter === 'all') {
        this.historyPage = 1;
        this.page = 1;
        this.mockInterviewPage = 1;
        this.fetchInterviewHistory();
        this.getAllMockInterViewHistory();
      } else if (this.historyFilter === 'company') {
        this.mockInterviewPage = 1;
        this.getAllMockInterViewHistory();
      } else if (this.historyFilter === 'ai') {
        this.page = 1;
        this.fetchInterviewHistory();
      }
    } else {
      this.fetchMockInterviews();
    }
  }

  toggleHistoryFilterDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isHistoryFilterOpen = !this.isHistoryFilterOpen;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.isHistoryFilterOpen = false;
  }

  getHistoryFilterLabel(): string {
    switch (this.historyFilter) {
      case 'company':
        return 'Company Specific';
      case 'ai':
        return 'AI Mock Interview';
      case 'all':
      default:
        return 'All';
    }
  }

  onHistoryFilterChange(filter: 'all' | 'company' | 'ai'): void {
    this.historyFilter = filter;
    this.isHistoryFilterOpen = false;
    if (this.activeTab !== 'history') {
      this.activeTab = 'history';
    }
    this.updateCombinedHistory();

    if (filter === 'all') {
      this.historyPage = 1;
      this.page = 1;
      this.mockInterviewPage = 1;
      this.fetchInterviewHistory();
      this.getAllMockInterViewHistory();
    } else if (filter === 'company') {
      this.mockInterviewPage = 1;
      this.getAllMockInterViewHistory();
    } else if (filter === 'ai') {
      this.page = 1;
      this.fetchInterviewHistory();
    }
  }

  getEffectiveTotalRecords(): number {
    if (this.historyFilter === 'company') {
      return this.mockInterviewTotalRecords;
    }
    if (this.historyFilter === 'ai') {
      return this.totalRecords;
    }
    return this.totalRecords + this.mockInterviewTotalRecords;
  }

  getEffectivePageSize(): number {
    if (this.historyFilter === 'company') {
      return this.mockInterviewPageSize;
    }
    if (this.historyFilter === 'ai') {
      return this.pageSize;
    }
    return this.historyPageSize;
  }

  getEffectiveFirst(): number {
    if (this.historyFilter === 'company') {
      return (this.mockInterviewPage - 1) * this.mockInterviewPageSize;
    }
    if (this.historyFilter === 'ai') {
      return (this.page - 1) * this.pageSize;
    }
    return (this.historyPage - 1) * this.historyPageSize;
  }

  onUnifiedPageChange(event: any): void {
    const newPage = event.page + 1;
    const newRows = event.rows;

    if (this.historyFilter === 'all') {
      this.historyPage = newPage;
      this.historyPageSize = newRows;
      this.page = newPage;
      this.pageSize = newRows;
      this.mockInterviewPage = newPage;
      this.mockInterviewPageSize = newRows;
      this.fetchInterviewHistory();
      this.getAllMockInterViewHistory();
    } else if (this.historyFilter === 'company') {
      this.mockInterviewPage = newPage;
      this.mockInterviewPageSize = newRows;
      this.getAllMockInterViewHistory();
    } else if (this.historyFilter === 'ai') {
      this.page = newPage;
      this.pageSize = newRows;
      this.fetchInterviewHistory();
    }
  }

  getHeaderTitle(): string {
    if (this.historyFilter === 'company') {
      return 'Company Specific Mock Interview';
    }
    if (this.historyFilter === 'ai') {
      return 'AI Mock Interview';
    }
    return 'Interview History';
  }

  getHeaderSubtitle(): string {
    if (this.historyFilter === 'company') {
      return 'Review your company-specific mock interview practice sessions and results';
    }
    if (this.historyFilter === 'ai') {
      return 'Track your AI mock interview progress and review past performances';
    }
    return 'Track your progress and review past performances';
  }

  get isHistoryLoading(): boolean {
    if (this.historyFilter === 'all') {
      return this.loading || this.mockInterviewLoading;
    }
    if (this.historyFilter === 'company') {
      return this.mockInterviewLoading;
    }
    return this.loading;
  }

  get isHistoryEmpty(): boolean {
    return !this.combinedHistory || this.combinedHistory.length === 0;
  }

  setupMockSearchTrigger(): void {
    this.mockSearchTrigger$
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => {
        this.mockPage = 1;
        this.fetchMockInterviews();
      });
  }

  fetchMockInterviews(): void {
    this.mockLoading = true;
    let url = `${environment.hrmsApiUrl}/rounds?is_public=true&publish_status=Published&page=${this.mockPage}&limit=${this.mockPageSize}`;
    if (this.mockSelectedCategory && this.mockSelectedCategory !== 'All') {
      url += '&interview_category=' + encodeURIComponent(this.mockSelectedCategory);
    }
    if (this.mockSearchTerm?.trim()) {
      url += '&search=' + encodeURIComponent(this.mockSearchTerm.trim());
    }
    this.http.get<any>(url)
      .pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        const dataObj = res?.Data || res?.data;
        if (dataObj && dataObj.rounds) {
          this.mockInterviews = dataObj.rounds;
            this.mockTotalRecords = dataObj.pagination?.total ?? dataObj.rounds.length;
          } else {
            this.mockInterviews = [];
            this.mockTotalRecords = 0;
        }
        this.mockLoading = false;
      },
      error: (err) => {
        console.error('Failed to fetch mock interviews', err);
        this.mockLoading = false;
          this.mockInterviews = [];
          this.mockTotalRecords = 0;
          this.toast.error('Failed to load practice interviews');
      }
    });
  }

  onMockPageChange(event: any): void {
    this.mockPage = event.page + 1;
    this.mockPageSize = event.rows;
    this.fetchMockInterviews();
  }

  onMockSearchChange(): void {
    this.mockSearchTrigger$.next();
  }

  onMockCategoryChange(): void {
    this.mockPage = 1;
    this.fetchMockInterviews();
  }

  clearMockFilters(): void {
    this.mockSearchTerm = '';
    this.mockSelectedCategory = 'All';
    this.mockPage = 1;
    this.fetchMockInterviews();
  }

  clearHistorySearch(): void {
    this.searchTerm = '';
    this.onSearchChange();
  }

  onSearchChange(): void {
    this.page = 1;
    this.mockInterviewPage = 1;
    this.historyPage = 1;
    this.updateCombinedHistory();
    this.refreshTrigger$.next();
  }

  onRoundChange(): void {
    this.page = 1;
    this.fetchInterviewHistory();
  }

  onPageChange(event: any): void {
    this.onUnifiedPageChange(event);
  }


  startMockInterview(roundId: string): void {
    if (!roundId) {
      this.toast.error('Invalid interview selected');
      return;
    }

    const interview = this.mockInterviews.find(i => i.id === roundId || i._id === roundId || i.screening_round_id === roundId);
    if (!interview) {
      this.toast.error('Interview not found');
      return;
    }
    
    let token = interview.unique_token;
    if (!token && interview.interview_url) {
      const parts = interview.interview_url.split('/');
      // Format is typically http://.../interview.ai/{token}/{id}
      if (parts.length >= 2) {
        token = parts[parts.length - 2];
      }
    }
    
    if (token) {
      const cleanRoundId = (roundId || '').split('?')[0].split('%3F')[0];
      const urlTree = this.router.createUrlTree(['/interview.ai', token, cleanRoundId], { queryParams: { is_b2c_practice: 'true' } });
      const url = this.router.serializeUrl(urlTree);
      window.open(url, '_blank');
    } else {
      this.toast.error('Interview link is unavailable. Please try again later.');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
